import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import SideBar from '../../components/Topbar';
import { HTMLCourseNavProvider } from "../../components/SidebarCourseContext";
import './assets/styles/HTMLLesson1.css';
import { CopyBlock, dracula } from "react-code-blocks";

// Import sophisticated icons
import { 
  FaCode,
  FaCheck,
  FaArrowRight,
  FaPlay,
  FaBookOpen,
  FaLayerGroup,
  FaPalette,
  FaPaintBrush,
  FaFillDrip,
  FaAccessibleIcon,
  FaExclamationTriangle,
  FaCircle,
  FaTint,
  FaBrush
} from 'react-icons/fa';

const HTMLLesson13 = () => {
  const [activeSection, setActiveSection] = useState(0);
  const [isCompleted, setIsCompleted] = useState(false);
  const [viewedSections, setViewedSections] = useState(new Set([0]));
  const sectionRefs = useRef([]);
  const observerRef = useRef(null);

  const [language] = useState("html");
  const [lineNumbers] = useState(true);

  // Video explanation URL
  const videoUrl = "https://app.codeflarelimited.com/watch-video/html-course";

  const code1 = `<!-- Using color names -->
<h1 style="color: Tomato;">Tomato Text</h1>
<p style="background-color: DodgerBlue; color: white;">
  DodgerBlue Background
</p>
<div style="border: 2px solid Violet;">
  Violet Border
</div>

<!-- Common color names -->
<span style="color: red;">Red</span>
<span style="color: blue;">Blue</span>
<span style="color: green;">Green</span>
<span style="color: purple;">Purple</span>
<span style="color: orange;">Orange</span>`;

  const code2 = `<!-- RGB (Red, Green, Blue) -->
<p style="color: rgb(255, 0, 0);">Red Text - rgb(255, 0, 0)</p>
<p style="color: rgb(0, 255, 0);">Green Text - rgb(0, 255, 0)</p>
<p style="color: rgb(0, 0, 255);">Blue Text - rgb(0, 0, 255)</p>

<!-- RGBA (with transparency) -->
<div style="background-color: rgba(255, 0, 0, 0.3);">
  Semi-transparent Red (30% opacity)
</div>
<div style="background-color: rgba(0, 255, 0, 0.7);">
  Semi-transparent Green (70% opacity)
</div>

<!-- Gray scales -->
<p style="color: rgb(128, 128, 128);">Medium Gray</p>
<p style="color: rgb(0, 0, 0);">Black</p>
<p style="color: rgb(255, 255, 255); background: black;">White</p>`;

  const code3 = `<!-- HEX Colors (6-digit) -->
<h2 style="color: #ff0000;">Red - #ff0000</h2>
<h2 style="color: #00ff00;">Green - #00ff00</h2>
<h2 style="color: #0000ff;">Blue - #0000ff</h2>

<!-- HEX Colors (3-digit shorthand) -->
<p style="background-color: #f00;">Red Background - #f00</p>
<p style="background-color: #0f0;">Green Background - #0f0</p>
<p style="background-color: #00f;">Blue Background - #00f</p>

<!-- Common HEX colors -->
<div style="color: #ff5733;">Coral - #ff5733</div>
<div style="color: #33ff57;">Spring Green - #33ff57</div>
<div style="color: #3357ff;">Royal Blue - #3357ff</div>
<div style="color: #f333ff;">Magenta - #f333ff</div>`;

  const code4 = `<!-- HSL (Hue, Saturation, Lightness) -->
<div style="color: hsl(0, 100%, 50%);">
  Red - hsl(0, 100%, 50%)
</div>
<div style="color: hsl(120, 100%, 50%);">
  Green - hsl(120, 100%, 50%)
</div>
<div style="color: hsl(240, 100%, 50%);">
  Blue - hsl(240, 100%, 50%)
</div>

<!-- HSLA (with transparency) -->
<div style="background-color: hsla(0, 100%, 50%, 0.3);">
  Semi-transparent Red - hsla(0, 100%, 50%, 0.3)
</div>

<!-- Adjusting saturation and lightness -->
<p style="color: hsl(0, 50%, 50%);">Less saturated red</p>
<p style="color: hsl(0, 100%, 25%);">Darker red</p>
<p style="color: hsl(0, 100%, 75%);">Lighter red</p>`;

  const code5 = `<!-- Using currentColor -->
<div style="color: blue; border: 2px solid currentColor;">
  Border inherits blue color
</div>

<button style="color: green; background-color: currentColor;">
  Button with currentColor background
</button>

<!-- currentColor in SVG -->
<svg width="100" height="100">
  <circle cx="50" cy="50" r="40" 
          fill="currentColor" />
</svg>

<!-- Practical example -->
<div class="card" style="color: #2563eb;">
  <h3 style="border-bottom: 2px solid currentColor;">
    Card Title
  </h3>
  <p>Card content with consistent coloring</p>
  <button style="background: currentColor; color: white;">
    Action Button
  </button>
</div>`;

  const code6 = `<!DOCTYPE html>
<html>
<head>
  <style>
    :root {
      /* CSS Custom Properties for consistent theming */
      --primary: #3b82f6;
      --secondary: #10b981;
      --accent: #8b5cf6;
      --danger: #ef4444;
      --warning: #f59e0b;
      --dark: #1f2937;
      --light: #f8fafc;
    }
    
    .color-system {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
      gap: 1rem;
      padding: 2rem;
    }
    
    .color-card {
      padding: 1rem;
      border-radius: 8px;
      text-align: center;
      color: white;
      font-weight: bold;
      box-shadow: 0 2px 4px rgba(0,0,0,0.1);
    }
    
    .color-card.dark {
      color: #1f2937;
    }
    
    .color-values {
      font-size: 0.8rem;
      opacity: 0.9;
      margin-top: 0.5rem;
    }
  </style>
</head>
<body>
  <div class="color-system">
    <!-- Color Names -->
    <div class="color-card" style="background: Tomato;">
      Tomato
      <div class="color-values">Color Name</div>
    </div>
    
    <!-- HEX Colors -->
    <div class="color-card" style="background: #3b82f6;">
      Primary Blue
      <div class="color-values">HEX: #3b82f6</div>
    </div>
    
    <!-- RGB Colors -->
    <div class="color-card" style="background: rgb(16, 185, 129);">
      Success Green
      <div class="color-values">RGB: 16, 185, 129</div>
    </div>
    
    <!-- HSL Colors -->
    <div class="color-card" style="background: hsl(262, 83%, 58%);">
      Accent Purple
      <div class="color-values">HSL: 262, 83%, 58%</div>
    </div>
    
    <!-- RGBA Colors -->
    <div class="color-card" style="background: rgba(239, 68, 68, 0.8);">
      Danger Red (80%)
      <div class="color-values">RGBA: 239, 68, 68, 0.8</div>
    </div>
    
    <!-- Light color -->
    <div class="color-card dark" style="background: #f8fafc;">
      Light Gray
      <div class="color-values">HEX: #f8fafc</div>
    </div>
  </div>

  <!-- Practical usage section -->
  <section style="padding: 2rem; background: var(--light);">
    <h2 style="color: var(--dark);">Color in Action</h2>
    
    <!-- Buttons with different color formats -->
    <div style="display: flex; gap: 1rem; flex-wrap: wrap; margin: 1rem 0;">
      <button style="background: var(--primary); color: white; padding: 0.5rem 1rem; border: none; border-radius: 4px;">
        Primary Button
      </button>
      
      <button style="background: hsl(142, 76%, 36%); color: white; padding: 0.5rem 1rem; border: none; border-radius: 4px;">
        Success (HSL)
      </button>
      
      <button style="background: rgb(245, 158, 11); color: white; padding: 0.5rem 1rem; border: none; border-radius: 4px;">
        Warning (RGB)
      </button>
    </div>
    
    <!-- Text with different colors -->
    <div style="display: flex; gap: 2rem; flex-wrap: wrap;">
      <span style="color: #dc2626;">Error Text</span>
      <span style="color: #059669;">Success Text</span>
      <span style="color: #d97706;">Warning Text</span>
      <span style="color: #2563eb;">Info Text</span>
    </div>
  </section>
</body>
</html>`;

  const sections = [
    {
      title: "HTML Colors Introduction",
      content: "Colors bring life to web pages and are specified using various formats including names, RGB, HEX, and HSL values",
      keyPoints: [
        "140+ predefined color names (Tomato, DodgerBlue, etc.)",
        "RGB for red-green-blue combinations",
        "HEX codes for web-standard colors",
        "HSL for intuitive color selection"
      ],
      icon: <FaPalette className="section-icon" />,
      color: "#2563eb",
      code: code1
    },
    {
      title: "RGB and RGBA Colors", 
      content: "RGB uses red, green, and blue light combinations, while RGBA adds alpha transparency for see-through effects",
      keyPoints: [
        "Syntax: rgb(red, green, blue)",
        "Values: 0-255 for each color",
        "RGBA adds alpha channel: 0.0-1.0",
        "Perfect for overlays and transparency effects"
      ],
      icon: <FaTint className="section-icon" />,
      color: "#dc2626",
      code: code2
    },
    {
      title: "HEX Color Codes",
      content: "Hexadecimal codes represent RGB values in a compact format, widely used in web development for consistency",
      keyPoints: [
        "Format: #RRGGBB (6 digits)",
        "Shorthand: #RGB (3 digits expands to 6)",
        "Each pair represents red, green, blue",
        "00 (lowest) to FF (highest) values"
      ],
      icon: <FaPaintBrush className="section-icon" />,
      color: "#059669",
      code: code3
    },
    {
      title: "HSL and HSLA Colors",
      content: "HSL represents colors through Hue, Saturation, and Lightness - a more intuitive way to work with colors",
      keyPoints: [
        "Hue: 0-360 degrees (color wheel)",
        "Saturation: 0%-100% (intensity)",
        "Lightness: 0%-100% (brightness)",
        "HSLA adds transparency like RGBA"
      ],
      icon: <FaFillDrip className="section-icon" />,
      color: "#7c3aed",
      code: code4
    },
    {
      title: "CurrentColor Keyword",
      content: "The currentColor keyword refers to the current text color, enabling consistent theming and inheritance",
      keyPoints: [
        "Inherits the current element's color",
        "Perfect for borders, backgrounds, SVGs",
        "Maintains consistency in components",
        "Reduces code duplication"
      ],
      icon: <FaAccessibleIcon className="section-icon" />,
      color: "#d97706",
      code: code5
    }
  ];

  // Intersection Observer for scroll detection
  useEffect(() => {
    const options = {
      root: null,
      rootMargin: '-10% 0px -20% 0px',
      threshold: 0.6
    };

    observerRef.current = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const index = sectionRefs.current.indexOf(entry.target);
        
        if (entry.isIntersecting) {
          setActiveSection(index);
          setViewedSections(prev => new Set([...prev, index]));
        }
      });
    }, options);

    sectionRefs.current.forEach((section) => {
      if (section) {
        observerRef.current.observe(section);
      }
    });

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  const addSectionRef = (element, index) => {
    sectionRefs.current[index] = element;
  };

  const getCompletionPercentage = () => {
    const totalSections = sections.length + 3;
    const viewedCount = viewedSections.size;
    return Math.round((viewedCount / totalSections) * 100);
  };

  return (
    <HTMLCourseNavProvider>
      <div className="html-lesson-container">
        <SideBar />
        
        <div className="html-lesson-main">
          {/* Header Section */}
          <div className="html-lesson-header">
            <div className="html-header-content">
              <div className="html-lesson-badge">
                <FaBookOpen className="html-badge-icon" />
                Lesson 13 • {getCompletionPercentage()}% Viewed
              </div>
              <h1 className="html-lesson-title">HTML Colors Mastery</h1>
              <p className="html-lesson-subtitle">
                Master color specification in HTML using names, RGB, HEX, HSL, and advanced color techniques
              </p>
              
              {/* Progress Bar */}
              <div className="html-completion-progress">
                <div 
                  className="html-completion-fill"
                  style={{ width: `${getCompletionPercentage()}%` }}
                ></div>
                <div className="html-progress-labels">
                  <span>Getting Started</span>
                  <span>Complete</span>
                </div>
              </div>
            </div>
          </div>

          {/* Progress Indicator */}
          <div className="html-progress-indicator">
            {sections.map((section, index) => (
              <div 
                key={index}
                className={`html-progress-step ${viewedSections.has(index) ? 'html-viewed' : ''} ${index === activeSection ? 'html-active' : ''}`}
              >
                <div className="html-step-number">
                  {viewedSections.has(index) ? '✓' : index + 1}
                </div>
                <div className="html-step-label">{section.title.split(' ')[0]}</div>
                {index < sections.length - 1 && <div className="html-step-connector"></div>}
              </div>
            ))}
          </div>

          {/* Visual Color Demonstration */}
          <div 
            className="html-visual-class"
            ref={(el) => addSectionRef(el, 0)}
          >
            <div className="html-visual-header">
              <div className="html-code-badge">
                <FaLayerGroup className="html-badge-icon" />
                Color Format Power
              </div>
              <h3 className="html-visual-title">
                Multiple Ways to Express Colors in HTML and CSS
              </h3>
            </div>
            
            <div className="html-class-demo">
              <div className="html-demo-code">
                <h4>Color Syntax Examples</h4>
                <div className="html-code-block-small">
                  <CopyBlock
                    language="css"
                    text={`/* Color Names */
color: Tomato;
color: DodgerBlue;

/* HEX Codes */
color: #ff0000;
color: #00ff00;

/* RGB Values */
color: rgb(255, 0, 0);
color: rgba(255, 0, 0, 0.5);

/* HSL Values */
color: hsl(0, 100%, 50%);
color: hsla(0, 100%, 50%, 0.5);`}
                    showLineNumbers={false}
                    theme={dracula}
                    wrapLines={true}
                    codeBlock
                  />
                </div>
              </div>
              
              <div className="html-demo-visual">
                <h4>Color Format Comparison</h4>
                <div className="html-color-preview">
                  <div className="color-comparison-item">
                    <div className="color-swatch name" style={{backgroundColor: 'Tomato'}}></div>
                    <span>Tomato (Name)</span>
                  </div>
                  <div className="color-comparison-item">
                    <div className="color-swatch hex" style={{backgroundColor: '#ff6347'}}></div>
                    <span>#ff6347 (HEX)</span>
                  </div>
                  <div className="color-comparison-item">
                    <div className="color-swatch rgb" style={{backgroundColor: 'rgb(255, 99, 71)'}}></div>
                    <span>rgb(255, 99, 71)</span>
                  </div>
                  <div className="color-comparison-item">
                    <div className="color-swatch hsl" style={{backgroundColor: 'hsl(9, 100%, 64%)'}}></div>
                    <span>hsl(9, 100%, 64%)</span>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="html-demo-note">
              <FaExclamationTriangle className="html-note-icon" />
              <p><strong>Pro Tip:</strong> All these formats produce the exact same Tomato color! Choose based on your project needs and team preferences.</p>
            </div>
          </div>

          {/* Content Sections */}
          <div className="html-content-sections">
            {sections.map((section, index) => (
              <div 
                key={index}
                ref={(el) => addSectionRef(el, index + 1)}
                className={`html-content-card ${viewedSections.has(index + 1) ? 'html-viewed' : ''} ${(index + 1) === activeSection ? 'html-active' : ''}`}
              >
                <div className="html-card-badge">
                  {viewedSections.has(index + 1) ? '✓ Viewed' : `Part ${index + 1}`}
                </div>
                
                <div className="html-card-header">
                  <div className="html-card-icon" style={{ backgroundColor: section.color }}>
                    {section.icon}
                  </div>
                  <div className="html-card-text">
                    <h2 className="html-card-title">{section.title}</h2>
                    <p className="html-card-content">{section.content}</p>
                    
                    <div className="html-key-points">
                      <h4>Key Points:</h4>
                      <ul>
                        {section.keyPoints.map((point, idx) => (
                          <li key={idx}>{point}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Code Example */}
                {section.code && (
                  <div className="html-section-code">
                    <div className="html-code-badge">
                      <FaCode className="html-badge-icon" />
                      Example
                    </div>
                    <div className="html-code-block-small">
                      <CopyBlock
                        language={language}
                        text={section.code}
                        showLineNumbers={lineNumbers}
                        theme={dracula}
                        wrapLines={true}
                        codeBlock
                      />
                    </div>
                  </div>
                )}
                
                <div className="html-card-actions">
                  <a 
                    href={videoUrl} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="html-action-btn"
                  >
                    <FaPlay className="html-btn-icon" />
                    Watch Video Explanation
                  </a>
                </div>
              </div>
            ))}
          </div>

          {/* Color Format Comparison */}
          <div 
            className="html-comparison-section"
            ref={(el) => addSectionRef(el, sections.length + 1)}
          >
            <div className="html-comparison-header">
              <div className="html-code-badge">
                <FaPalette className="html-badge-icon" />
                Color Format Comparison
              </div>
              <h3 className="html-comparison-title">
                When to Use Each Color Format
              </h3>
            </div>
            
            <div className="html-comparison-table">
              <table>
                <thead>
                  <tr>
                    <th>Format</th>
                    <th>Syntax</th>
                    <th>Best For</th>
                    <th>Pros</th>
                    <th>Cons</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td><strong>Color Names</strong></td>
                    <td><code>Tomato, DodgerBlue</code></td>
                    <td>Prototyping, learning</td>
                    <td>Human readable, easy to remember</td>
                    <td>Limited selection (140 colors)</td>
                  </tr>
                  <tr>
                    <td><strong>HEX</strong></td>
                    <td><code>#ff0000, #f00</code></td>
                    <td>Web development, design systems</td>
                    <td>Compact, widely supported, consistent</td>
                    <td>Less intuitive, hard to modify</td>
                  </tr>
                  <tr>
                    <td><strong>RGB/RGBA</strong></td>
                    <td><code>rgb(255,0,0)</code></td>
                    <td>Programmatic color generation</td>
                    <td>Intuitive, easy transparency</td>
                    <td>Longer syntax, less compact</td>
                  </tr>
                  <tr>
                    <td><strong>HSL/HSLA</strong></td>
                    <td><code>hsl(0,100%,50%)</code></td>
                    <td>Design adjustments, themes</td>
                    <td>Very intuitive, easy to modify</td>
                    <td>Less common in legacy code</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Color Psychology Section */}
          <div 
            className="html-practices-section"
            ref={(el) => addSectionRef(el, sections.length + 2)}
          >
            <div className="html-practices-header">
              <div className="html-code-badge">
                <FaCheck className="html-badge-icon" />
                Color Psychology & Best Practices
              </div>
              <h3 className="html-practices-title">
                Using Colors Effectively in Web Design
              </h3>
            </div>
            
            <div className="html-practices-grid">
              <div className="html-practice-card html-do">
                <h4>✅ Color Best Practices</h4>
                <ul>
                  <li>Use consistent color palette throughout site</li>
                  <li>Ensure sufficient color contrast for accessibility</li>
                  <li>Use semantic colors (red for errors, green for success)</li>
                  <li>Test colors on different devices and lighting</li>
                  <li>Consider color blindness (8% of men)</li>
                </ul>
              </div>
              
              <div className="html-practice-card html-dont">
                <h4>❌ Common Color Mistakes</h4>
                <ul>
                  <li>Using too many bright colors together</li>
                  <li>Poor contrast between text and background</li>
                  <li>Using color alone to convey information</li>
                  <li>Ignoring cultural color associations</li>
                  <li>Not testing for color blindness compatibility</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Color Wheel Demonstration */}
          <div 
            className="html-multiple-classes"
            ref={(el) => addSectionRef(el, sections.length + 3)}
          >
            <div className="html-multiple-header">
              <div className="html-code-badge">
                <FaCircle className="html-badge-icon" />
                HSL Color Wheel
              </div>
              <h3 className="html-multiple-title">
                Understanding HSL Through the Color Wheel
              </h3>
            </div>
            
            <div className="html-multiple-content">
              <div className="html-multiple-explanation">
                <h4>HSL Components Explained:</h4>
                <ul>
                  <li><strong>Hue (0-360°):</strong> Position on color wheel
                    <ul>
                      <li>0° = Red</li>
                      <li>120° = Green</li>
                      <li>240° = Blue</li>
                      <li>360° = Back to Red</li>
                    </ul>
                  </li>
                  <li><strong>Saturation (0%-100%):</strong> Color intensity
                    <ul>
                      <li>0% = Gray</li>
                      <li>100% = Full color</li>
                    </ul>
                  </li>
                  <li><strong>Lightness (0%-100%):</strong> Brightness level
                    <ul>
                      <li>0% = Black</li>
                      <li>50% = Normal color</li>
                      <li>100% = White</li>
                    </ul>
                  </li>
                </ul>
              </div>
              
              <div className="html-hsl-demo">
                <h4>HSL Color Examples:</h4>
                <div className="hsl-examples">
                  <div className="hsl-item" style={{backgroundColor: 'hsl(0, 100%, 50%)'}}>
                    <span>Red<br/>hsl(0, 100%, 50%)</span>
                  </div>
                  <div className="hsl-item" style={{backgroundColor: 'hsl(120, 100%, 50%)'}}>
                    <span>Green<br/>hsl(120, 100%, 50%)</span>
                  </div>
                  <div className="hsl-item" style={{backgroundColor: 'hsl(240, 100%, 50%)'}}>
                    <span>Blue<br/>hsl(240, 100%, 50%)</span>
                  </div>
                  <div className="hsl-item" style={{backgroundColor: 'hsl(60, 100%, 50%)'}}>
                    <span>Yellow<br/>hsl(60, 100%, 50%)</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Comprehensive Example */}
          <div 
            className="html-code-example"
            ref={(el) => addSectionRef(el, sections.length + 4)}
          >
            <div className="html-code-header">
              <div className="html-code-badge">
                <FaCode className="html-badge-icon" />
                Complete Example
              </div>
              <h3 className="html-code-title">
                Professional Color System Implementation
              </h3>
            </div>
            <div className="html-code-block">
              <CopyBlock
                language={language}
                text={code6}
                showLineNumbers={lineNumbers}
                theme={dracula}
                wrapLines={true}
                codeBlock
              />
            </div>
            <div className="html-code-actions">
              <a 
                href={videoUrl} 
                target="_blank" 
                rel="noopener noreferrer"
                className="html-code-btn html-primary"
              >
                Watch Complete Color Implementation Guide
              </a>
            </div>
          </div>

          {/* Action Section */}
          <div 
            className="html-action-section"
            ref={(el) => addSectionRef(el, sections.length + 5)}
          >
            <div className="html-action-header">
              <h3>Mastered HTML colors?</h3>
              <div className="html-completion-badge">
                {getCompletionPercentage()}% of content viewed
              </div>
            </div>
            
            <div className="html-action-content">
              <p>You've learned comprehensive color specification including color names, HEX codes, RGB/RGBA, HSL/HSLA, and the currentColor keyword. Start creating vibrant, accessible web designs!</p>
              
              <div className="html-action-buttons">
                <button 
                  className={`html-complete-button ${isCompleted ? 'html-completed' : ''}`}
                  onClick={() => setIsCompleted(true)}
                >
                  {isCompleted ? (
                    <>
                      <FaCheck className="html-button-icon" />
                      Lesson Completed
                    </>
                  ) : (
                    'Mark as Complete'
                  )}
                </button>
                
                {isCompleted && (
                  <Link to="/html-links" className="html-next-lesson-link">
                    Continue to Next Lesson
                    <FaArrowRight className="html-link-icon" />
                  </Link>
                )}
              </div>
              
              <div className="html-quick-nav">
                <span>Need more examples? </span>
                <a 
                  href={videoUrl} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="html-nav-link"
                >
                  Watch Complete HTML Course
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </HTMLCourseNavProvider>
  );
}

export default HTMLLesson13;